// src/components/ComponentLifing.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  orderBy,
  increment,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  Chip,
  Badge,
  Autocomplete,
  Alert,
  ListItemButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CachedIcon from '@mui/icons-material/Cached'; 
import PostAddIcon from '@mui/icons-material/PostAdd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// --- Componente Modale (MODIFICATO con t()) ---
const AddToWorkOrderModal = ({ open, onClose, teamId, vehicleId, item, onAssign, itemType }) => {
  // --- MODIFICA: Inizializza t() (usa namespace 'lifing') ---
  const { t, i18n } = useTranslation('lifing');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && vehicleId && teamId) {
      setIsLoading(true);
      const q = query(
        collection(db, "workOrders"),
        where("teamId", "==", teamId),
        where("vehicleId", "==", vehicleId),
        where("status", "==", "Open")
      );
      const unsub = onSnapshot(q, (snapshot) => {
        setWorkOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      }, (error) => {
        console.error("Error fetching open work orders:", error);
        setIsLoading(false);
      });
      return () => unsub();
    }
  }, [open, vehicleId, teamId]);

  const handleAssign = async (workOrder) => {
    if (!item) return;
    setIsSaving(true);
    try {
      await onAssign(workOrder, item);
      onClose();
    } catch (error) {
      console.error("Error assigning to W.O.:", error);
      alert(t('modal_wo_assign_error')); // Tradotto
    } finally {
      setIsSaving(false);
    }
  };
  
  const title = itemType === 'Lifing' ? t('modal_wo_title') : t('modal_wo_title_default');
  const getLocaleFormat = () => (currentLang === 'it' ? 'it-IT' : 'en-GB');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {!isLoading && workOrders.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('modal_wo_empty')}
          </Alert>
        )}
        {!isLoading && (
          <List>
            {workOrders.map(wo => (
              <ListItem key={wo.id} disablePadding>
                <ListItemButton onClick={() => handleAssign(wo)} disabled={isSaving}>
                  <ListItemIcon><PostAddIcon /></ListItemIcon>
                  <ListItemText 
                    primary={wo.title} 
                    secondary={`${t('modal_wo_created')} ${wo.createdAt ? new Date(wo.createdAt.toDate()).toLocaleDateString(getLocaleFormat()) : '...'}`} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        <Button 
          variant="outlined" 
          onClick={() => navigate('/workshop/new')} 
          sx={{ mt: 2 }}
        >
          {t('modal_wo_new_button')}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('modal_wo_cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};


// Helper function (INVARIATA)
const getUsageStatus = (percentage) => { 
  if (percentage >= 100) return { color: 'error', level: 'critical' };
  if (percentage >= 90) return { color: 'error', level: 'high' };
  if (percentage >= 75) return { color: 'warning', level: 'medium' };
  return { color: 'success', level: 'low' };
};


function ComponentLifing({ vehicleId, teamId }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('lifing');
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [components, setComponents] = useState([]);
  const [isLoadingComponents, setIsLoadingComponents] = useState(true);
  const [inventoryList, setInventoryList] = useState([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [newCompName, setNewCompName] = useState('');
  const [newCompLife, setNewCompLife] = useState('');
  const [newCompUnit, setNewCompUnit] = useState('km');
  const [linkedInventoryItem, setLinkedInventoryItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [usageToAdd, setUsageToAdd] = useState('');
  const [currentComponent, setCurrentComponent] = useState(null);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [componentToReplace, setComponentToReplace] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [woModalOpen, setWoModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  // useEffects (INVARIATI)
  useEffect(() => {
    if (!vehicleId) return; setIsLoadingComponents(true);
    const q = query(collection(db,"vehicleComponents"), where("vehicleId","==",vehicleId), orderBy("createdAt","asc"));
    const unsub = onSnapshot(q, (qs) => { setComponents(qs.docs.map(d => ({ id: d.id, ...d.data() }))); setIsLoadingComponents(false); }, (err) => setIsLoadingComponents(false));
    return () => unsub();
  }, [vehicleId]);
  useEffect(() => {
    if (!teamId) return; setIsLoadingInventory(true);
    const q = query(collection(db,"inventory"), where("teamId","==",teamId), orderBy("name","asc"));
    const unsub = onSnapshot(q, (qs) => { setInventoryList(qs.docs.map(d => ({ id: d.id, ...d.data() }))); setIsLoadingInventory(false); }, (err) => setIsLoadingInventory(false));
    return () => unsub();
  }, [teamId]);

  // --- MODIFICA: Funzioni (con t()) ---
  const handleAddComponent = async (e) => { 
    e.preventDefault(); const name = newCompName.trim(); const totalLife = Number(newCompLife);
    if (!name || !totalLife || totalLife <= 0) { 
      alert(t('error_add_fields')); // Tradotto
      return; 
    }
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "vehicleComponents"), {
        teamId: teamId, vehicleId: vehicleId, name: name, totalLife: totalLife, unit: newCompUnit, currentUsage: 0,
        inventoryItemId: linkedInventoryItem ? linkedInventoryItem.id : null,
        inventoryItemName: linkedInventoryItem ? linkedInventoryItem.name : null,
        createdAt: serverTimestamp(),
        status: 'Active'
      });
      setNewCompName(''); setNewCompLife(''); setNewCompUnit('km'); setLinkedInventoryItem(null);
    } catch (error) { 
      console.error("Error:", error); 
      alert(t('error_add_generic')); // Tradotto
    }
    setIsSubmitting(false);
  };
  
  const openUsageDialog = (component) => { setCurrentComponent(component); setUsageToAdd(''); setDialogOpen(true); };
  const closeUsageDialog = () => { setDialogOpen(false); setCurrentComponent(null); };
  
  const handleAddUsage = async () => { 
    const usage = Number(usageToAdd); if (!usage || usage <= 0 || !currentComponent) return;
    const compRef = doc(db, "vehicleComponents", currentComponent.id);
    try { await updateDoc(compRef, { currentUsage: increment(usage) }); closeUsageDialog(); }
    catch (error) { 
      console.error("Error:", error); 
      alert(t('usage_modal_error')); // Tradotto
    }
  };
  
  const handleDeleteComponent = async (componentId) => { 
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try { await deleteDoc(doc(db,"vehicleComponents",componentId)); } catch (err) { console.error(err); } 
    }
  };
  
  const handleOpenReplaceDialog = (component) => {
    setComponentToReplace(component);
    setReplaceDialogOpen(true);
  };
  const handleCloseReplaceDialog = () => {
    setReplaceDialogOpen(false);
    setComponentToReplace(null);
    setIsReplacing(false);
  };
  
  const handleConfirmReplace = async () => { 
    if (!componentToReplace) return;
    setIsReplacing(true);
    const compRef = doc(db, "vehicleComponents", componentToReplace.id);
    const linkedItemId = componentToReplace.inventoryItemId;
    try {
      const batch = writeBatch(db);
      batch.update(compRef, { currentUsage: 0, status: 'Active' }); 
      if (linkedItemId) {
        const itemInStock = inventoryList.find(item => item.id === linkedItemId);
        if (itemInStock && itemInStock.quantity > 0) {
          const inventoryRef = doc(db, "inventory", linkedItemId);
          batch.update(inventoryRef, { quantity: increment(-1) });
        } else {
          console.warn("Replacement executed without reducing stock (quantity 0 or item not found).");
        }
      }
      await batch.commit();
      handleCloseReplaceDialog();
    } catch (error) {
      console.error("Error during replacement:", error);
      alert(t('replace_modal_error')); // Tradotto
      setIsReplacing(false);
    }
  };

  const handleOpenWOModal = (component) => {
    setSelectedComponent(component);
    setWoModalOpen(true);
  };
  const handleCloseWOModal = () => {
    setWoModalOpen(false);
    setSelectedComponent(null);
  };

  const handleAssignToWorkOrder = async (workOrder, component) => {
    const batch = writeBatch(db);
    
    const newItemRef = doc(collection(db, "workOrders", workOrder.id, "items"));
    batch.set(newItemRef, {
      teamId: teamId,
      workOrderId: workOrder.id,
      source: 'Lifing',
      sourceId: component.id,
      description: t('assign_wo_desc', { name: component.name }), // Tradotto
      status: "Open",
      createdAt: serverTimestamp(),
      notes: t('assign_wo_notes', { currentUsage: component.currentUsage, totalLife: component.totalLife, unit: component.unit }), // Tradotto
      parts: component.inventoryItemName || '',
      hours: 0,
    });
    
    const componentRef = doc(db, "vehicleComponents", component.id);
    batch.update(componentRef, { status: 'In Workshop' });
    
    await batch.commit();
  };
  // --- FINE MODIFICA ---

  const isLoading = isLoadingComponents || isLoadingInventory;
  const activeComponents = components.filter(c => c.status !== 'In Workshop' && c.status !== 'Risolto');
  const workshopComponents = components.filter(c => c.status === 'In Workshop');

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        
        {/* Section 1: Add New Component (con t()) */}
        <Box component="form" onSubmit={handleAddComponent} sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>{t('title')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField
              label={t('name_label')}
              value={newCompName}
              onChange={(e) => setNewCompName(e.target.value)}
              required
              fullWidth
              placeholder={t('name_placeholder')}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={inventoryList}
              getOptionLabel={(opt) => `${opt.name}${opt.code ? ` (${opt.code})` : ''}`}
              value={linkedInventoryItem}
              onChange={(e, val) => setLinkedInventoryItem(val)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('inventory_label')}
                  placeholder={t('inventory_search')}
                />
              )}
              disabled={isLoadingInventory}
              fullWidth
              sx={{ flex: 2 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField
              label={t('life_label')}
              type="number"
              value={newCompLife}
              onChange={(e) => setNewCompLife(e.target.value)}
              required
              fullWidth
              sx={{ flex: 1 }}
            />
            <FormControl fullWidth sx={{ width: { xs: '100%', md: 180 } }}>
              <InputLabel id="unit-label">{t('unit_label')}</InputLabel>
              <Select
                labelId="unit-label"
                value={newCompUnit}
                label={t('unit_label')}
                onChange={(e) => setNewCompUnit(e.target.value)}
              >
                <MenuItem value="km">km</MenuItem>
                <MenuItem value="hours">hours</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isSubmitting} 
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} 
            sx={{ mt: 1, height: '56px' }}
            size="large"
          >
            {t('track_button')}
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />

        {/* Section 2: Component List (con t()) */}
        <Box>
          <Typography variant="h5" gutterBottom>{t('active_title')}</Typography>
          {isLoading && <CircularProgress size={24} />}
          {!isLoading && activeComponents.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('active_empty')}</Typography> )}
          
          <List>
            {activeComponents.map((comp) => {
              const usagePercent = (comp.totalLife > 0) ? (comp.currentUsage / comp.totalLife) * 100 : 0;
              const status = getUsageStatus(usagePercent);
              
              let stockQuantity = null;
              if (comp.inventoryItemId) {
                  const linkedItem = inventoryList.find(item => item.id === comp.inventoryItemId);
                  stockQuantity = linkedItem ? linkedItem.quantity : '?';
              }

              return (
                <ListItem key={comp.id} divider
                  sx={{ backgroundColor: status.level === 'high' || status.level === 'critical' ? 'rgba(211, 47, 47, 0.1)' : 'transparent', paddingRight: { xs: 1, sm: 2 } }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5 }}>
                      {stockQuantity !== null && (
                        <Tooltip title={t('stock_tooltip', { name: comp.inventoryItemName || 'N/A' })}>
                           <Badge 
                             badgeContent={stockQuantity} 
                             color={stockQuantity === '?' || stockQuantity <= 0 ? "error" : "success"}
                             aria-label={stockQuantity === '?' || stockQuantity <= 0 ? t('stock_badge_error', { count: stockQuantity }) : t('stock_badge_ok', { count: stockQuantity })}
                           >
                             <WarehouseIcon color="action" />
                           </Badge>
                        </Tooltip>
                      )}
                      <Tooltip title={t('wo_tooltip')}>
                        <IconButton edge="end" aria-label="add-to-wo" onClick={() => handleOpenWOModal(comp)}>
                          <PostAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('replace_tooltip')}>
                        <IconButton edge="end" aria-label="replace" onClick={() => handleOpenReplaceDialog(comp)}>
                          <CachedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('usage_tooltip')}><IconButton edge="end" onClick={() => openUsageDialog(comp)}><HistoryIcon /></IconButton></Tooltip>
                      <Tooltip title={t('delete_tooltip')}><IconButton edge="end" onClick={() => handleDeleteComponent(comp.id)}><DeleteIcon /></IconButton></Tooltip>
                     </Box>
                  }
                >
                  {(status.level === 'high' || status.level === 'critical') && ( <ListItemIcon sx={{ minWidth: 40 }}><Tooltip title={status.level === 'critical' ? t('status_tooltip_critical') : t('status_tooltip_high')}><WarningAmberIcon color="error" /></Tooltip></ListItemIcon> )}
                  
                  <ListItemText
                    primary={comp.name}
                    secondary={
                      <>
                        {comp.inventoryItemName && <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t('inventory_ref')} {comp.inventoryItemName}</Typography>}
                        <Box sx={{ width: '100%', pt: 0.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <LinearProgress variant="determinate" value={usagePercent > 100 ? 100 : usagePercent} color={status.color} sx={{ height: 6, borderRadius: 5, flexGrow: 1 }}/>
                          <Chip label={`${usagePercent.toFixed(1)}%`} color={status.color} size="small" variant="outlined"/>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>{`${comp.currentUsage} / ${comp.totalLife} ${comp.unit}`}</Typography>
                        </Box>
                      </>
                    }
                    sx={{ ml: (status.level === 'high' || status.level === 'critical') ? 0 : 5, pr: { xs: 16, sm: 22 } }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
        
        {/* Sezione "In Workshop" (con t()) */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="h5" gutterBottom>{t('workshop_title')}</Typography>
          {!isLoading && workshopComponents.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('workshop_empty')}
            </Typography>
          )}
          <List dense>
            {workshopComponents.map((comp) => (
              <ListItem key={comp.id} divider>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AssignmentTurnedInIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={comp.name}
                  secondary={t('workshop_status', { currentUsage: comp.currentUsage, totalLife: comp.totalLife, unit: comp.unit })}
                  sx={{ color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Dialog "Add Usage" (con t()) */}
      <Dialog open={dialogOpen} onClose={closeUsageDialog}>
        <DialogTitle>{t('usage_modal_title', { name: currentComponent?.name })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('usage_modal_subtitle', { currentUsage: currentComponent?.currentUsage, unit: currentComponent?.unit })}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="usage"
            label={t('usage_modal_label', { unit: currentComponent?.unit })}
            type="number"
            fullWidth
            variant="standard"
            value={usageToAdd}
            onChange={(e) => setUsageToAdd(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUsageDialog}>{t('modal_wo_cancel')}</Button>
          <Button onClick={handleAddUsage}>{t('usage_modal_add_button')}</Button>
        </DialogActions>
      </Dialog>
            
      {/* Dialog "Replace" (con t() e <Trans>) */}
      <Dialog open={replaceDialogOpen} onClose={handleCloseReplaceDialog}>
        <DialogTitle>{t('replace_modal_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Trans 
              t={t}
              i18nKey="replace_modal_subtitle1"
              values={{ name: componentToReplace?.name }}
              components={{ strong: <strong /> }}
            />
          </DialogContentText>
          <DialogContentText component="div" sx={{ mt: 1 }}>
            <Trans 
              t={t}
              i18nKey="replace_modal_subtitle2"
              values={{ currentUsage: componentToReplace?.currentUsage, unit: componentToReplace?.unit }}
              components={{ strong: <strong /> }}
            />
          </DialogContentText>
          
          {componentToReplace?.inventoryItemId ? (
            (() => {
              const item = inventoryList.find(i => i.id === componentToReplace.inventoryItemId);
              if (item && item.quantity > 0) {
                return (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Trans 
                      t={t}
                      i18nKey="replace_modal_stock_ok"
                      values={{ name: item.name, q_current: item.quantity, q_new: item.quantity - 1 }}
                      components={{ strong: <strong /> }}
                    />
                  </Alert>
                );
              } else if (item) {
                return (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Trans 
                      t={t}
                      i18nKey="replace_modal_stock_zero"
                      values={{ name: item.name }}
                      components={{ strong: <strong /> }}
                    />
                  </Alert>
                );
              } else {
                return (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {t('replace_modal_stock_not_found', { id: componentToReplace.inventoryItemId })}
                  </Alert>
                );
              }
            })()
          ) : (
             <Alert severity="info" sx={{ mt: 2 }}>
               {t('replace_modal_stock_unlinked')}
             </Alert>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplaceDialog} disabled={isReplacing}>{t('modal_wo_cancel')}</Button>
          <Button onClick={handleConfirmReplace} disabled={isReplacing} variant="contained" color="primary">
            {isReplacing ? <CircularProgress size={24} /> : t('replace_modal_confirm_button')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <AddToWorkOrderModal
        open={woModalOpen}
        onClose={handleCloseWOModal}
        teamId={teamId}
        vehicleId={vehicleId}
        item={selectedComponent}
        onAssign={handleAssignToWorkOrder}
        itemType="Lifing"
      />
    </>
  );
}

export default ComponentLifing;